import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMars,
  faVenus,
  faCompass,
  faNewspaper,
  faAward,
  faStar,
  faSquare,
  faRedo,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment/moment';
import axios from 'axios';
import { isEqual } from 'lodash';
import Dropdown from 'react-bootstrap/Dropdown';
import Select from 'react-select';
import {
  ADMIN_PREFIX, BOOST_ICON_IMAGE,
  PUPPY_DEFAULT_IMAGE,
} from '../../../../utils/constants';
import { AdInterface } from '../../admin-dashboard-tables/interfaces';
import { formatCurrency } from '../../utils';
import DateHelper from '../../../../utils/DateHelper';
import useModal from '../../../../utils/hooks/useModal';
import ConfirmationModal from '../../widgets/ConfirmationModal';
import EditSVG from '../../admin-dashboard-tables/assets/EditSVG';
import { cloudinaryTransform } from '../../../../utils/cloudinaryTransformations';
import routes from '../../../../utils/routes';
import BoostedBadge from './BoostedBadge';
import AdditionalInfoTextArea from './AdditionalInfoTextArea';

interface Props extends AdInterface {
  id: number,
  status: string,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
  allowBulkSelect?: boolean,
  onToggleExtraCallback?: (extraData?: any) => any,
  userIsApprovedSeller?: boolean,
}

const AdHit: React.FC<Props> = ({
  objectID,
  id,
  status,
  admin_breed_display,
  name,
  thumbnailUrl,
  route,
  sex,
  price,
  sale_price,
  availability,
  expires_at,
  ad_type,
  created_at,
  sold_at,
  view_count,
  listings_type,
  message_count,
  city,
  state,
  selected,
  toggleSelected,
  allowBulkSelect,
  trackSelected,
  setTrackedSelected,
  userIsApprovedSeller,
  boost_expires_at,
  is_boosted,
}) => {
  const [windowHref, setWindowHref] = useState(window.location.href);
  const [actionType, setActionType] = useState('');
  const now = Number(moment().utc().format('X'));
  const isExpired = now && expires_at && expires_at < now;

  useEffect(() => {
    let newWindowHref = window.location.href;
    newWindowHref = newWindowHref.replace(/&/g, '|amp|');
    if (windowHref !== newWindowHref) {
      setWindowHref(newWindowHref);
    }
  }, [windowHref, window.location.href]);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    setModalFormState,
    modalFormState,
  } = useModal();

  const updateModal = (
    action: string,
    fields: object,
  ) => {
    if (action !== 'mark as \'sold\'') {
      setActionType('');
    }
    if (action === '') {
      setModalData({
        message: '',
        onClose: () => {
        },
      });
    } else if (action === 'renew') {
      setModalData({
        message: `Would you like to ${action} the ad for ${name}? Please note, you can only renew one ad at a time.`,
        onConfirmAsync: () => {
          return new Promise((res, rej) => {
            axios.post(routes.renewSingleListing(listings_type, id))
              .then(({ data }) => {
                window.location = data.redirect;
              })
              .catch((e) => {
                if (e.response.status === 422) {
                  const errors = e.response.data?.errors || {};
                  const message = Object.keys(errors)
                    .map((errorKey) => errors[errorKey].join(' '))
                    .join(' ');
                  if (message) {
                    e.response.data.message = message;
                  }
                  rej(e);
                } else if (e.response.status === 404) {
                  e.response.data.message = `Could not find the ad for ${name}`;
                  rej(e);
                } else {
                  rej(e);
                }
              });
          });
        },
        onClose: () => {
          updateModal('', {});
        },
      });
    } else {
      setModalData({
        message: `Would you like to ${action} this ad for ${name}?`,
        ModalForm: ({
          formState,
          chosenAction,
        }) => {
          useEffect(() => {
            if (formState.availability !== 'sold') {
              setModalFormState((prevState) => ({
                ...prevState,
                sold_through: '',
                sold_through_additional_info: '',
              }));
            }
          }, [formState.availability]);

          if (chosenAction === 'mark-as-sold') {
            return (
              <div>
                <div className="my-1">How was puppy sold?</div>
                <Select
                  placeholder="Choose an option..."
                  onChange={(e) => {
                    setModalFormState((prevState) => ({
                      ...prevState,
                      sold_through: e?.value,
                    }));
                  }}
                  options={[
                    {
                      label: 'Lancaster Puppies',
                      value: 'lancaster_puppies',
                    },
                    {
                      label: 'Other Advertising Site',
                      value: 'other_site',
                    },
                    {
                      label: 'Word of Mouth',
                      value: 'word_of_mouth',
                    },
                    {
                      label: 'Other',
                      value: 'other',
                    },
                  ]}
                />
                <AdditionalInfoTextArea
                  formState={formState}
                  onChange={(e) => {
                    setModalFormState((prevState) => ({
                      ...prevState,
                      sold_through_additional_info: e.target.value,
                    }));
                  }}
                />
              </div>
            );
          }
          return null;
        },
        onConfirmAsync: (additionalFields) => {
          return new Promise((res, rej) => {
            axios.patch(
              `${routes.LISTINGS_ALL_BULK_UPDATE}`,
              {
                listings: [{
                  id,
                  listings_type,
                }],
                fields: { ...fields, ...additionalFields },
              },
            )
              .then(({ data }) => {
                res(data.message);
              })
              .catch((e) => {
                rej(e);
              });
          });
        },
        onClose: () => {
          setModalFormState({});
          updateModal('', {});
        },
        onSuccess: () => {
          updateModal('', {}); // TODO: Can maybe remove?
          setShowModal(false);
          window.location.reload();
        },
      });
    }
  };

  const onToggleExtraCallback = useCallback((add: boolean) => {
    if (trackSelected && setTrackedSelected) {
      const newSelected = [...trackSelected.filter((item) => item.id !== id)];
      if (add) {
        newSelected.push({
          id,
          listings_type,
        });
      }
      if (!isEqual(newSelected, trackSelected)) {
        setTrackedSelected(newSelected);
      }
    }
  }, [trackSelected, setTrackedSelected]);

  return (
    <>
      <div className={`d-flex border-bottom py-2 listing-row ${status}`}>
        <div className="d-flex td-image-checkbox">
          <div className="td-checkbox my-auto mx-2 d-flex align-items-center">
            {allowBulkSelect && (
              <input
                type="checkbox"
                checked={selected.includes(id)}
                onChange={() => {
                  const currentlySelected = selected.includes(id);
                  toggleSelected(id);
                  if (onToggleExtraCallback) {
                    onToggleExtraCallback(!currentlySelected);
                  }
                }}
              />
            )}
            <div className="td-image ml-1">
              <img
                className="rounded img-fluid"
                height="60"
                width="60"
                alt={`${name}, a ${admin_breed_display} puppy.`}
                src={
                  thumbnailUrl
                    ? cloudinaryTransform(
                      thumbnailUrl,
                      [
                        't_lpr-full-transform',
                        'c_thumb,h_60,w_60',
                      ],
                    ) : PUPPY_DEFAULT_IMAGE
                }
              />
            </div>
          </div>
        </div>
        <div className="d-flex listing-wrapper">
          <div className="td-info-container px-2 td-info w-100">
            <div className="info-left">
              <a
                className="text-auto"
                href={route}
              >
                <p className="my-1 puppy-name text-auto">
                  {name}
                </p>
              </a>
              {status === 'pending_approval' && (
                <p className="mb-0 text-muted">(Pending Approval)</p>
              )}
              <p className="mb-0 text-muted text-auto mr-2">
                {
                  sex.toLowerCase() === 'male'
                    ? (<FontAwesomeIcon icon={faMars} className="my-auto mr-2 male-icon" />)
                    : (<FontAwesomeIcon icon={faVenus} className="my-auto mr-2 female-icon" />)
                }
                <small>{admin_breed_display}</small>
              </p>
              <p className="mb-0 text-muted">
                <FontAwesomeIcon icon={faCompass} className="my-auto mr-2" size="sm" />
                <small>
                  {city}
                  ,
                  {' '}
                  {state}
                </small>
              </p>
            </div>
            <div className="info-right">
              <p className="mb-0 puppy-name">
                {formatCurrency(price, true)}
              </p>
              <p className="mb-0">
                <FontAwesomeIcon icon={faNewspaper} className="menu-item-icon mr-2" />
                {message_count || 0}
                {' '}
                Messages
              </p>
              <p className="mb-0 w-100">
                {ad_type === 'premium' && (
                  <FontAwesomeIcon icon={faAward} className="text-warning mr-2" size="xs" />
                )}
                {ad_type === 'advanced' && (
                  <FontAwesomeIcon icon={faStar} className="text-advanced-green mr-2" size="xs" />
                )}
                {ad_type === 'basic' && (
                  <FontAwesomeIcon icon={faSquare} className="text-primary mr-2" size="xs" rotate={45} />
                )}
                {`${view_count} Views`}
              </p>
              {is_boosted && (<BoostedBadge />)}
            </div>
          </div>
          <div className="d-flex justify-content-between flex-fill td-dates-buttons w-100">
            <div className="mb-2 my-sm-auto px-2 text-auto">
              {availability === 'sold' ? (
                <>
                  {sold_at && (
                    <p className="mb-0 text-muted text-auto">
                      <small>
                        Sold:
                        {' '}
                        {DateHelper.getLocalizedDateTimeFromTimestamp(sold_at).format('M/D/Y h:mm:ss A')}
                      </small>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="mb-0 text-muted text-auto">
                    <small>
                      Posted:
                      {' '}
                      {DateHelper.getLocalizedDateTimeFromTimestamp(created_at).format('M/D/Y h:mm:ss A')}
                    </small>
                  </p>
                  {expires_at && (
                    <p className="mb-0 text-muted text-auto">
                      <small>
                        {
                          isExpired
                            ? (<span className="text-dark font-weight-bolder">Expired: </span>)
                            : 'Expires: '
                        }
                        {DateHelper.getLocalizedDateTimeFromTimestamp(expires_at).format('M/D/Y h:mm:ss A')}
                      </small>
                    </p>
                  )}
                  {boost_expires_at && is_boosted && (
                    <p className="mb-0 text-muted text-auto">
                      <small>
                        Boost expires:
                        {` ${DateHelper.getLocalizedDateTimeFromTimestamp(boost_expires_at).format('M/D/Y h:mm:ss A')}`}
                      </small>
                    </p>
                  )}
                </>
              )}
              {availability === 'pending' && (<p className="text-success m-0"><small>Pending Sale!</small></p>)}
            </div>
            <div className="my-auto d-flex justify-content-end flex-wrap action-buttons-container">
              {!isExpired && status === 'published' && availability === 'available' && (
                <a
                  href={`${ADMIN_PREFIX}boost/${listings_type}/${id}`}
                  className="mx-2 my-auto btn badge badge-pill font-family-sans-serif my-2 boost-listing-button"
                >
                  <span className="d-flex align-items-center p-1">
                    <img src={BOOST_ICON_IMAGE} alt="Boost" />
                    <span className="ml-1">Boost</span>
                  </span>
                </a>
              )}
              <a
                href={`${ADMIN_PREFIX}edit/${listings_type}/${id}/?redirect=${windowHref}`}
                className="mx-2 my-auto btn badge badge-pill badge-blue-200 font-family-sans-serif border my-2"
              >
                <span className="d-flex align-items-center p-1">
                  <EditSVG />
                  <span className="ml-1">Edit Ad</span>
                </span>
              </a>
              {!isExpired && (
                <Dropdown
                  className="mx-2 my-auto"
                  drop="down"
                >
                  <Dropdown.Toggle
                    className="p-2 btn btn-primary badge badge-pill font-family-sans-serif border actions-button"
                    id={`${objectID}-dropdown-actions-button`}
                  >
                    Actions
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {availability !== 'sold' && (
                      <Dropdown.Item
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setActionType('mark-as-sold');
                          updateModal(
                            'mark as \'sold\'',
                            { availability: 'sold' },
                          );
                        }}
                      >
                        Set As Sold
                      </Dropdown.Item>
                    )}
                    {availability !== 'pending' && (
                      <Dropdown.Item
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          updateModal(
                            'mark as \'pending\'',
                            { availability: 'pending' },
                          );
                        }}
                      >
                        Set As Pending
                      </Dropdown.Item>
                    )}
                    {availability !== 'available' && (
                      <Dropdown.Item
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          updateModal(
                            'mark as \'available\'',
                            { availability: 'available' },
                          );
                        }}
                      >
                        Set As Available
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              {isExpired
                ? (
                  <a
                    href="#"
                    className="text-dark my-2 btn badge action-button"
                    onClick={(e) => {
                      e.preventDefault();
                      updateModal('renew', {});
                    }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faRedo} className="mx-1" />
                      Relist
                    </span>
                  </a>
                ) : (
                  <>
                    {status !== 'draft' && (
                      <a
                        href="#"
                        className={`my-2 btn badge action-button ${status === 'published' ? 'text-danger' : 'text-primary'}`}
                        aria-label={`${(status === 'published' || status === 'pending_approval') ? 'Delete' : 'Publish'} listing with name: ${name}.`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (status === 'published' || status === 'pending_approval') {
                            updateModal(
                              'delete',
                              { status: 'unpublished' },
                            );
                          } else {
                            updateModal(
                              'publish',
                              {
                                status: userIsApprovedSeller
                                  ? 'published'
                                  : 'pending_approval',
                              },
                            );
                          }
                        }}
                      >
                        {(status === 'published' || status === 'pending_approval') ? 'Delete' : 'Publish'}
                      </a>
                    )}
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        title="Confirm Action"
        show={showModal}
        setShow={setShowModal}
        actionType={actionType}
        modalFormState={modalFormState}
        {...modalData}
      />
    </>
  );
};

export default AdHit;
